import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';

import { ChangeEmailPhoneModel } from '../../../../models/core/change-email-phone.model';
import { DeliveryMethodModel } from '../../../../models/delivery-method.model';
import { DeliveryMethodEnum } from '../../../../models/enums/delivery-method.enum';
import { BasicDataService } from '../../../../services/basic-data.service';
import { GeneralMeetingsResource } from '../../../../services/resources/general-meetings.resource';
import { ShareholdersResource } from '../../../../services/resources/shareholders.resource';
import { SharedBusinessLogicService } from '../../../../services/shared-business-logic.service';
import { ChangeEmailPhoneComponent } from '../../../dialogs/change-email-phone/change-email-phone.component';

@Component({
    selector: 'sl-self-attendance',
    templateUrl: './self-attendance.dialog.html',
    styleUrls: ['./self-attendance.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SelfAttendanceDialog implements OnInit {
    isLoading = true;

    private deliveryMethod?: DeliveryMethodModel;
    private allowElectronicAdmissionCard = false;
    private shouldSkipMobileValidation = false;

    private readonly shareholdersResource = inject(ShareholdersResource);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly basicDataService = inject(BasicDataService);
    private readonly sharedBusinessLogicService = inject(SharedBusinessLogicService);
    private readonly matDialog = inject(MatDialog);
    private readonly matDialogRef = inject(MatDialogRef);

    get actionTitleKey(): string {
        if (this.allowElectronicAdmissionCard && this.deliveryMethod?.deliveryMethodId === DeliveryMethodEnum.Electronic) {
            return 'GeneralMeeting.SelfAttendEmail';
        }
        return 'GeneralMeeting.SelfAttendPost';
    }

    get shouldAskToSwitchToEmail(): boolean {
        return this.allowElectronicAdmissionCard && this.deliveryMethod?.deliveryMethodId !== DeliveryMethodEnum.Electronic;
    }

    ngOnInit(): void {
        forkJoin({
            deliveryMethod: this.shareholdersResource.getDeliveryMethodForCurrent(),
            basicData: this.basicDataService.get()
        }).subscribe(({ deliveryMethod, basicData }) => {
            this.deliveryMethod = deliveryMethod.value;
            this.allowElectronicAdmissionCard = basicData.allowElectronicAdmissionCard ?? false;
            this.shouldSkipMobileValidation = basicData.mobileConfirmationDisabled;
            this.isLoading = false;
        });
    }

    switchToElectronicDelivery(): void {
        // Both the email and mobile (if required) should be confirmed, if not we need to start the confirmation process.
        if (!this.deliveryMethod?.emailConfirmed || (!this.deliveryMethod.mobileNumberConfirmed && !this.shouldSkipMobileValidation)) {
            const dialogRef = this.matDialog.open(ChangeEmailPhoneComponent, {
                minWidth: '30%',
                data: new ChangeEmailPhoneModel({
                    isEmailConfirmed: this.deliveryMethod?.emailConfirmed,
                    isPhoneConfirmed: this.deliveryMethod?.mobileNumberConfirmed,
                    validateEmailOnly: this.shouldSkipMobileValidation
                })
            });

            dialogRef.afterClosed().subscribe(isDone => {
                if (isDone) {
                    this.setDeliveryMethodElectronic();
                }
            });

            return;
        }

        this.setDeliveryMethodElectronic();
    }

    selfRegister(): void {
        this.isLoading = true;
        this.generalMeetingsResource
            .selfRegister()
            .subscribe()
            // always close and reload, maybe the register was successful but the email failed
            .add(() => this.matDialogRef.close(true));
    }

    private setDeliveryMethodElectronic(): void {
        this.isLoading = true;
        this.shareholdersResource
            .setDeliveryMethodForCurrent(DeliveryMethodEnum.Electronic)
            .subscribe()
            .add(() => this.reloadDeliveryMethod());
    }

    private reloadDeliveryMethod(): void {
        this.isLoading = true;
        this.shareholdersResource
            .getDeliveryMethodForCurrent()
            .subscribe(response => (this.deliveryMethod = response.value))
            .add(() => (this.isLoading = false));
        this.sharedBusinessLogicService.reloadDeliveryInformation();
    }
}
